import { useEffect, useState } from "react";
import axios from "axios";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { useSiteData } from "../../context/SiteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium outline-none transition focus:border-[var(--brand)] focus:ring-2 focus:ring-[var(--brand)]/20";

const Group = ({ title, children }) => (
  <div className="rounded-3xl bg-white border border-slate-100 p-6 sm:p-8 space-y-5 shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)]">
    <h3 className="font-display font-bold text-lg text-[#0b132b]">{title}</h3>
    {children}
  </div>
);

const T = ({ label, value, onChange, textarea, testid }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
    {textarea ? (
      <textarea data-testid={testid} rows={textarea === true ? 3 : textarea} value={value} onChange={onChange} className={`${inputCls} mt-1.5 resize-none`} />
    ) : (
      <input data-testid={testid} value={value} onChange={onChange} className={`${inputCls} mt-1.5`} />
    )}
  </label>
);

export const WebsiteTab = () => {
  const { content, refreshContent } = useSiteData();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!form) {
      setForm({
        ...content,
        marquee_text: (content.marquee_items || []).join("\n"),
        why_items: (content.why_items || []).map((i) => ({ ...i })),
        services_items: (content.services_items || []).map((i) => ({ ...i })),
        about_stats: (content.about_stats || []).map((s) => ({ ...s })),
      });
    }
  }, [content, form]);

  if (!form) {
    return <div className="w-10 h-10 rounded-full border-4 border-[var(--brand)]/20 border-t-[var(--brand)] animate-spin" data-testid="website-loading-spinner" />;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setWhy = (i, k) => (e) => {
    const why_items = form.why_items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it));
    setForm({ ...form, why_items });
  };
  const setService = (i, k) => (e) => {
    const services_items = form.services_items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it));
    setForm({ ...form, services_items });
  };
  const setStat = (i, k) => (e) => {
    const about_stats = form.about_stats.map((s, j) => (j === i ? { ...s, [k]: k === "value" ? Number(e.target.value) : e.target.value } : s));
    setForm({ ...form, about_stats });
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.marquee_items = form.marquee_text.split("\n").map((s) => s.trim()).filter(Boolean);
      delete payload.marquee_text;
      await axios.put(`${API}/content`, payload, { withCredentials: true });
      await refreshContent();
      toast.success("Website content updated — live on the site.");
    } catch {
      toast.error("Could not save the content.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="admin-website-form">
      <Group title="Hero Section">
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Badge text" value={form.hero_badge} onChange={set("hero_badge")} testid="we-hero-badge" />
          <T label="Button 1 label" value={form.hero_btn_primary} onChange={set("hero_btn_primary")} testid="we-hero-btn1" />
          <T label="Headline — line 1" value={form.hero_line1} onChange={set("hero_line1")} testid="we-hero-line1" />
          <T label="Headline — line 2 (accent)" value={form.hero_line2} onChange={set("hero_line2")} testid="we-hero-line2" />
          <T label="Button 2 label" value={form.hero_btn_secondary} onChange={set("hero_btn_secondary")} testid="we-hero-btn2" />
          <T label="Hero image alt text" value={form.hero_image_alt} onChange={set("hero_image_alt")} testid="we-hero-alt" />
        </div>
        <T label="Subtext" value={form.hero_subtext} onChange={set("hero_subtext")} textarea={3} testid="we-hero-subtext" />
        <T label="Hero image URL" value={form.hero_image} onChange={set("hero_image")} testid="we-hero-image" />
        {form.hero_image && !form.hero_video && <img src={form.hero_image} alt="Hero preview" className="w-full h-36 object-cover rounded-xl border border-slate-100" />}
        <T label="Hero video URL (optional — replaces the image; upload in Media, paste URL here)" value={form.hero_video || ""} onChange={set("hero_video")} testid="we-hero-video" />
        {form.hero_video && (
          <video src={form.hero_video} poster={form.hero_image} muted loop autoPlay playsInline className="w-full h-36 object-cover rounded-xl border border-slate-100" data-testid="we-hero-video-preview" />
        )}
      </Group>

      <Group title="Marquee Ribbon">
        <T label="Phrases — one per line" value={form.marquee_text} onChange={set("marquee_text")} textarea={5} testid="we-marquee" />
      </Group>

      <Group title="Tours Section Header">
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Eyebrow" value={form.tours_eyebrow} onChange={set("tours_eyebrow")} testid="we-tours-eyebrow" />
          <T label="Title" value={form.tours_title} onChange={set("tours_title")} testid="we-tours-title" />
        </div>
        <T label="Subtitle" value={form.tours_subtitle} onChange={set("tours_subtitle")} textarea={2} testid="we-tours-subtitle" />
      </Group>

      <Group title="Services Section">
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Eyebrow" value={form.services_eyebrow} onChange={set("services_eyebrow")} testid="we-services-eyebrow" />
          <T label="Title" value={form.services_title} onChange={set("services_title")} testid="we-services-title" />
        </div>
        <T label="Subtitle" value={form.services_subtitle} onChange={set("services_subtitle")} textarea={2} testid="we-services-subtitle" />
        {(form.services_items || []).map((item, i) => (
          <div key={i} className="rounded-2xl border border-slate-100 p-4 space-y-3" data-testid={`we-service-item-${i}`}>
            <p className="text-xs font-bold text-slate-400 uppercase">Service {i + 1}</p>
            <T label="Title" value={item.title} onChange={setService(i, "title")} testid={`we-service-title-${i}`} />
            <T label="Text" value={item.copy} onChange={setService(i, "copy")} textarea={2} testid={`we-service-copy-${i}`} />
          </div>
        ))}
      </Group>

      <Group title="Destinations Section Header">
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Eyebrow" value={form.dest_eyebrow} onChange={set("dest_eyebrow")} testid="we-dest-eyebrow" />
          <T label="Title" value={form.dest_title} onChange={set("dest_title")} testid="we-dest-title" />
        </div>
        <T label="Subtitle" value={form.dest_subtitle} onChange={set("dest_subtitle")} textarea={2} testid="we-dest-subtitle" />
      </Group>

      <Group title="Why Choose Section">
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Eyebrow" value={form.why_eyebrow} onChange={set("why_eyebrow")} testid="we-why-eyebrow" />
          <T label="Title" value={form.why_title} onChange={set("why_title")} testid="we-why-title" />
        </div>
        {form.why_items.map((item, i) => (
          <div key={i} className="rounded-2xl border border-slate-100 p-4 space-y-3" data-testid={`we-why-item-${i}`}>
            <p className="text-xs font-bold text-slate-400 uppercase">Card {i + 1}</p>
            <T label="Title" value={item.title} onChange={setWhy(i, "title")} testid={`we-why-title-${i}`} />
            <T label="Text" value={item.copy} onChange={setWhy(i, "copy")} textarea={2} testid={`we-why-copy-${i}`} />
          </div>
        ))}
      </Group>

      <Group title="About Section">
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Eyebrow" value={form.about_eyebrow} onChange={set("about_eyebrow")} testid="we-about-eyebrow" />
          <T label="Title" value={form.about_title} onChange={set("about_title")} testid="we-about-title" />
        </div>
        <T label="Paragraph" value={form.about_copy} onChange={set("about_copy")} textarea={4} testid="we-about-copy" />
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Main image URL" value={form.about_image} onChange={set("about_image")} testid="we-about-image" />
          <T label="Small image URL" value={form.about_image2} onChange={set("about_image2")} testid="we-about-image2" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {form.about_stats.map((s, i) => (
            <div key={i} className="rounded-2xl border border-slate-100 p-4 grid grid-cols-3 gap-2" data-testid={`we-stat-${i}`}>
              <label className="block">
                <span className="text-[10px] font-bold uppercase text-slate-400">Value</span>
                <input type="number" value={s.value} onChange={setStat(i, "value")} className={`${inputCls} mt-1`} data-testid={`we-stat-value-${i}`} />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase text-slate-400">Suffix</span>
                <input value={s.suffix} onChange={setStat(i, "suffix")} className={`${inputCls} mt-1`} data-testid={`we-stat-suffix-${i}`} />
              </label>
              <label className="block">
                <span className="text-[10px] font-bold uppercase text-slate-400">Label</span>
                <input value={s.label} onChange={setStat(i, "label")} className={`${inputCls} mt-1`} data-testid={`we-stat-label-${i}`} />
              </label>
            </div>
          ))}
        </div>
      </Group>

      <Group title="Contact Section">
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Eyebrow" value={form.contact_eyebrow} onChange={set("contact_eyebrow")} testid="we-contact-eyebrow" />
          <T label="Title" value={form.contact_title} onChange={set("contact_title")} testid="we-contact-title" />
          <T label="Email" value={form.contact_email} onChange={set("contact_email")} testid="we-contact-email" />
          <T label="Phone" value={form.contact_phone} onChange={set("contact_phone")} testid="we-contact-phone" />
          <T label="Address" value={form.contact_address} onChange={set("contact_address")} testid="we-contact-address" />
        </div>
        <T label="Subtitle" value={form.contact_subtitle} onChange={set("contact_subtitle")} textarea={2} testid="we-contact-subtitle" />
        <T label="Hours" value={form.contact_hours} onChange={set("contact_hours")} testid="we-contact-hours" />
      </Group>

      <Group title="Closing Note (bottom of homepage)">
        <div className="grid sm:grid-cols-2 gap-4">
          <T label="Small photo URL (upload in Media, paste URL here)" value={form.closing_photo || ""} onChange={set("closing_photo")} testid="we-closing-photo" />
          <T label="Name / signature" value={form.closing_name || ""} onChange={set("closing_name")} testid="we-closing-name" />
        </div>
        {form.closing_photo && <img src={form.closing_photo} alt="Closing note preview" className="w-16 h-16 rounded-full object-cover border border-slate-100" data-testid="we-closing-photo-preview" />}
        <T label="Message" value={form.closing_text || ""} onChange={set("closing_text")} textarea={3} testid="we-closing-text" />
        <p className="text-xs text-slate-400">Clear both the photo URL and the message to hide this section.</p>
      </Group>

      <Group title="Footer">
        <T label="Tagline" value={form.footer_tagline} onChange={set("footer_tagline")} textarea={2} testid="we-footer-tagline" />
        <T label="Newsletter text" value={form.footer_newsletter} onChange={set("footer_newsletter")} testid="we-footer-newsletter" />
      </Group>

      <button
        data-testid="website-save-button"
        type="submit"
        disabled={busy}
        className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] text-white text-sm font-bold px-7 py-3.5 shadow-xl shadow-[var(--brand)]/30 transition-all duration-300 hover:bg-[var(--brand-dark)] disabled:opacity-60"
      >
        <Save className="w-4 h-4" />
        {busy ? "Saving…" : "Save All Changes"}
      </button>
    </form>
  );
};
